import isEqual from 'lodash/isEqual';
import {PosterOpType} from '@PosterWhiteboard/collaboration/poster-op.types';
import type {PosterOp, SnapshotRecord} from '@PosterWhiteboard/collaboration/poster-op.types';
import {diffRecords, hasRecordChanges, isSnapshotRecord} from '@PosterWhiteboard/collaboration/record-changes.library';
import {getItemsById, getItemsOrder, getPageOrder, getPageProps, getPagesById, getPosterProps} from '@PosterWhiteboard/collaboration/poster-snapshot.library';
import {getPrecedingId, insertIdAfter} from '@PosterWhiteboard/collaboration/id-order.library';

interface KeyedRecordsDiff {
  removedIds: string[];
  /** In the order they appear in the new id order, so each add can anchor on the one before it. */
  addedIds: string[];
  keptIds: string[];
}

/**
 * The ops that turn the `before` poster snapshot into `after`. Applying them to `before` with
 * `applyPosterOps` reproduces `after`; applying them to a poster other people have since changed
 * merges at leaf granularity.
 */
export const derivePosterOps = (before: SnapshotRecord, after: SnapshotRecord): PosterOp[] => {
  return [...derivePosterSetOps(before, after), ...derivePageOps(before, after)];
};

const derivePosterSetOps = (before: SnapshotRecord, after: SnapshotRecord): PosterOp[] => {
  const changes = diffRecords(getPosterProps(before), getPosterProps(after));
  return hasRecordChanges(changes) ? [{type: PosterOpType.POSTER_SET, changes}] : [];
};

const derivePageOps = (before: SnapshotRecord, after: SnapshotRecord): PosterOp[] => {
  const beforePagesById = getPagesById(before);
  const afterPagesById = getPagesById(after);
  const afterPageOrder = getPageOrder(after);
  const {removedIds, addedIds, keptIds} = diffKeyedRecords(beforePagesById, afterPagesById, afterPageOrder);
  const ops: PosterOp[] = [];

  // Adds go first: a poster can never be left without a page, so removing every old page before
  // the new ones exist would have the last remove refused.
  for (const pageId of addedIds) {
    const page = afterPagesById[pageId];
    if (isSnapshotRecord(page)) {
      ops.push({type: PosterOpType.PAGE_ADD, pageId, page, afterPageId: getPrecedingId(afterPageOrder, pageId)});
    }
  }
  for (const pageId of removedIds) {
    ops.push({type: PosterOpType.PAGE_REMOVE, pageId});
  }

  for (const pageId of keptIds) {
    const beforePage = beforePagesById[pageId];
    const afterPage = afterPagesById[pageId];
    if (beforePage !== afterPage && isSnapshotRecord(beforePage) && isSnapshotRecord(afterPage)) {
      ops.push(...deriveOpsWithinPage(pageId, beforePage, afterPage));
    }
  }

  if (!isEqual(predictOrderAfterAddsAndRemoves(getPageOrder(before), {removedIds, addedIds}, afterPageOrder), afterPageOrder)) {
    ops.push({type: PosterOpType.PAGE_ORDER_SET, pageOrder: afterPageOrder});
  }

  return ops;
};

const deriveOpsWithinPage = (pageId: string, beforePage: SnapshotRecord, afterPage: SnapshotRecord): PosterOp[] => {
  const ops: PosterOp[] = [];
  const pageChanges = diffRecords(getPageProps(beforePage), getPageProps(afterPage));
  if (hasRecordChanges(pageChanges)) {
    ops.push({type: PosterOpType.PAGE_SET, pageId, changes: pageChanges});
  }

  const beforeItemsById = getItemsById(beforePage);
  const afterItemsById = getItemsById(afterPage);
  const afterItemsOrder = getItemsOrder(afterPage);
  const {removedIds, addedIds, keptIds} = diffKeyedRecords(beforeItemsById, afterItemsById, afterItemsOrder);

  for (const itemId of removedIds) {
    ops.push({type: PosterOpType.ITEM_REMOVE, pageId, itemId});
  }

  for (const itemId of addedIds) {
    const item = afterItemsById[itemId];
    if (isSnapshotRecord(item)) {
      ops.push({type: PosterOpType.ITEM_ADD, pageId, itemId, item, afterItemId: getPrecedingId(afterItemsOrder, itemId)});
    }
  }

  for (const itemId of keptIds) {
    const beforeItem = beforeItemsById[itemId];
    const afterItem = afterItemsById[itemId];
    if (beforeItem === afterItem || !isSnapshotRecord(beforeItem) || !isSnapshotRecord(afterItem)) {
      continue;
    }
    const itemChanges = diffRecords(beforeItem, afterItem);
    if (hasRecordChanges(itemChanges)) {
      ops.push({type: PosterOpType.ITEM_SET, pageId, itemId, changes: itemChanges});
    }
  }

  if (!isEqual(predictOrderAfterAddsAndRemoves(getItemsOrder(beforePage), {removedIds, addedIds}, afterItemsOrder), afterItemsOrder)) {
    ops.push({type: PosterOpType.ITEMS_ORDER_SET, pageId, itemsOrder: afterItemsOrder});
  }

  return ops;
};

const diffKeyedRecords = (beforeById: SnapshotRecord, afterById: SnapshotRecord, afterOrder: string[]): KeyedRecordsDiff => {
  const afterIds = Object.keys(afterById);
  const orderedAddedIds = afterOrder.filter((id) => Object.hasOwn(afterById, id) && !Object.hasOwn(beforeById, id));
  const unorderedAddedIds = afterIds.filter((id) => !Object.hasOwn(beforeById, id) && !afterOrder.includes(id));

  return {
    removedIds: Object.keys(beforeById).filter((id) => !Object.hasOwn(afterById, id)),
    addedIds: [...orderedAddedIds, ...unorderedAddedIds],
    keptIds: afterIds.filter((id) => Object.hasOwn(beforeById, id)),
  };
};

/**
 * The order the add and remove ops alone will produce. An explicit order op is only emitted when
 * this differs from the real new order, so a plain add or delete never carries a whole-array
 * write that could overwrite someone else's concurrent reorder.
 */
const predictOrderAfterAddsAndRemoves = (beforeOrder: string[], {removedIds, addedIds}: Pick<KeyedRecordsDiff, 'removedIds' | 'addedIds'>, afterOrder: string[]): string[] => {
  const orderAfterRemoves = beforeOrder.filter((id) => !removedIds.includes(id));
  return addedIds.reduce((order, addedId) => insertIdAfter(order, addedId, getPrecedingId(afterOrder, addedId)), orderAfterRemoves);
};
